import { Transition } from "components/shared/column";
import FloatingVideo from "components/shared/floatingVideo";
import Loader from "components/shared/loader";
import {
    bidPointTypesEnum,
    catalogEntityOptionFlags,
    catalogEntityOptionFlagsEnum,
    entityTypesEnum,
    organizationRegistrationStatusesEnum,
} from "helpers/enums";
import { buildConfigByProperties, getInitialStateFromConfig } from "helpers/form";
import { isImpersonatingUser } from "helpers/localStorage";
import { paths } from "helpers/paths";
import { flagVal } from "helpers/system";
import { requiredField } from "helpers/validators";
import cloneDeep from "lodash/cloneDeep";
import policy from "policies";
import React, { useEffect, useState } from "react";
import { Redirect } from "react-router-dom";
import { getList, getProperties } from "sources/catalogs";
import { getCombinedProperties } from "sources/extendedProperties";
import { getOnboarding } from "sources/profile/user";
import FormBody from "./body";
import configs from "./config";
import { customConnect } from "./container";

const hiddenFlagVal = flagVal(catalogEntityOptionFlags, catalogEntityOptionFlagsEnum.Invisible);
const requiredFlagVal = flagVal(catalogEntityOptionFlags, catalogEntityOptionFlagsEnum.Required);
const showInEmptyMileageBidsFlagVal = flagVal(
    catalogEntityOptionFlags,
    catalogEntityOptionFlagsEnum.ShowInEmptyMileageBids,
);
const loadOptionsPropertyName = "loadOptions";

const entityTypesMappers = {
    [entityTypesEnum.Cargo]: "cargos",
    [entityTypesEnum.BidPoint]: "bidPoints",
};

const { cargoTruckConfig, mainConfig } = configs;

const catalogOptionShowsInEmptyMileageBids = (option) => {
    if (!option) {
        return false;
    }
    return (
        option.flags?.includes?.(catalogEntityOptionFlagsEnum.ShowInEmptyMileageBids) ||
        (showInEmptyMileageBidsFlagVal > 0 &&
            (option.intFlags & showInEmptyMileageBidsFlagVal) === showInEmptyMileageBidsFlagVal)
    );
};

const mergeCatalogOptionDefaults = (entityOptions, catalogProperties) => {
    const defaultOptionsById = catalogProperties.reduce((acc, catalog) => {
        (catalog.entityOptions || []).forEach((option) => {
            acc[option.id] = option;
        });
        return acc;
    }, {});

    return entityOptions.map((option) => ({
        ...option,
        showInEmptyMileageBids:
            catalogOptionShowsInEmptyMileageBids(option) ||
            catalogOptionShowsInEmptyMileageBids(defaultOptionsById[option.entityOptionId]),
    }));
};

const Form = (props) => {
    const shouldRedirect = !props.profile.canCreateBids && props.action === "new";
    const isOnboardingSuppressed = props.roles.isIntegrationAccount || isImpersonatingUser();
    const [propsConfigLoaded, setPropsConfigLoaded] = useState(false);
    const [extendedPropertiesConfig, setExtendedPropertiesConfig] = useState([]);
    const [entityOptions, setEntityOptions] = useState([]);
    const [onboardingOptions, setOnboardingOptions] = useState({});

    useEffect(() => {
        if (shouldRedirect) {
            return;
        }
        Promise.all([
            getCombinedProperties({ isRelation: false }),
            getList([entityTypesEnum.Cargo, entityTypesEnum.BidPoint, entityTypesEnum.TruckingBid]),
            getProperties(),
            isOnboardingSuppressed ? Promise.resolve({}) : getOnboarding(),
        ]).then(([extendedPropertiesConfig, entityOptions, catalogProperties, onboardingOptions]) => {
            setExtendedPropertiesConfig(extendedPropertiesConfig);
            setEntityOptions(mergeCatalogOptionDefaults(entityOptions, catalogProperties));
            setPropsConfigLoaded(true);
            setOnboardingOptions(onboardingOptions);
        });
    }, [shouldRedirect, isOnboardingSuppressed]);

    if (shouldRedirect) {
        return <Redirect to={paths.bid} />;
    }

    if (!propsConfigLoaded) {
        return (
            <Transition dir="full">
                <Loader prefix="absolute-center" />
            </Transition>
        );
    }
    const { action, isPublic, profile, roles } = props;

    const bidPolicy = policy("bid", { user: { profile, roles }, record: {}, options: { action, isPublic } });

    const config = cloneDeep({ ...mainConfig, ...(bidPolicy.isCargoTruck() ? cargoTruckConfig : {}) });
    config.extendedProperties = {
        type: "extendedProperty",
        config: buildConfigByProperties(extendedPropertiesConfig.bid),
    };
    config.bidPoints.config.extendedProperties = {
        type: "extendedProperty",
        config: buildConfigByProperties(extendedPropertiesConfig.bidPoint),
    };
    config.cargos.config.extendedProperties = {
        type: "extendedProperty",
        config: buildConfigByProperties(extendedPropertiesConfig.cargo),
    };
    entityOptions
        .filter((option) => !!option.propertyName)
        .forEach((option) => {
            const entityConfig = entityTypesMappers[option.entityType]
                ? config[entityTypesMappers[option.entityType]].config
                : config;
            const isRequired = (option.intFlags & requiredFlagVal) === requiredFlagVal;
            const isHidden = (option.intFlags & hiddenFlagVal) === hiddenFlagVal;
            const propertyName = option.propertyName.charAt(0).toLowerCase() + option.propertyName.slice(1);
            const isLoadOptions = propertyName === loadOptionsPropertyName;
            if (isRequired) {
                entityConfig[propertyName].required = true;
                entityConfig[propertyName].validators = [
                    ...(entityConfig[propertyName].validators || []),
                    (value, data) =>
                        data.type === bidPointTypesEnum.ServicePoint ||
                        (data.type === bidPointTypesEnum.CustomPoint && isLoadOptions)
                            ? ""
                            : requiredField(value),
                ];
            }
            if (isHidden && entityConfig[propertyName]) {
                entityConfig[propertyName].isHidden = true;
            }
            if (option.displayName && entityConfig[propertyName]?.inputProps) {
                entityConfig[propertyName].inputProps.label = option.displayName;
            }
        });
    const initialState = getInitialStateFromConfig(config);
    initialState.extendedProperties = getInitialStateFromConfig(config.extendedProperties.config, {
        isExtendedProp: true,
    });
    initialState.bidPoints.forEach((p) => {
        p.extendedProperties = getInitialStateFromConfig(config.bidPoints.config.extendedProperties.config, {
            isExtendedProp: true,
        });
    });
    initialState.cargos.forEach((p) => {
        p.extendedProperties = getInitialStateFromConfig(config.cargos.config.extendedProperties.config, {
            isExtendedProp: true,
        });
    });

    if (props.action === "new") {
        initialState.responsibleId = parseInt(profile.id);
    }
    if (initialState.carOption) {
        initialState.carOption.carId = props.carId ? parseInt(props.carId) : "";
    }
    return (
        <>
            {!isOnboardingSuppressed &&
                !onboardingOptions.isCreateBidButtonFinished &&
                props.organization?.registrationStatus === organizationRegistrationStatusesEnum.Completed && (
                    <FloatingVideo opened src={onboardingOptions.bidCreatingVideoLink} />
                )}
            <FormBody
                {...props}
                {...{ config, initialState, entityOptions, onboardingOptions, isOnboardingSuppressed }}
            />
        </>
    );
};

export default customConnect(Form, "bids");
